/**
 * RightPanelManager
 * Manages the right panel that slides in from the right side of the chat window.
 * Owns the panel header (title + back/options/primary/close buttons) and content area.
 * Includes resize functionality with draggable handle.
 *
 * @example
 * eventBus.emit('right-panel:open', {
 *   title: 'Session 935',
 *   titleAlign: 'left',                        // 'left' | 'center', default 'left'
 *   backButton: { onClick: fn } | null,
 *   starButton: {
 *     starred: boolean,
 *     onClick: (nextStarred) => boolean | Promise<boolean>  // resolves success
 *   } | null,
 *   optionsDropdown: {
 *     items: [{ action, label, variant }, ...],
 *     onAction: (action) => {...}
 *   } | null,
 *   secondaryButton: { label, onClick } | null,
 *   primaryButton: { label, onClick } | null,
 *   content: HTMLElement | string,
 *   contentType: 'session-detail'
 * });
 */
import { OptionsDropdown } from '../components/OptionsDropdown.js';
import { Tooltip } from '../utils/Tooltip.js';

// Properties the panel animates on its way out — `width` in the desktop
// split-view, `transform` in the mobile overlay (layout.css). Either one ending
// means the slide is done.
const CLOSE_TRANSITION_PROPERTIES = ['width', 'transform'];

// Backstop for the close transition, in case transitionend never arrives (the
// panel was already at its closed geometry, transitions disabled, a hidden tab).
// Comfortably longer than the 300ms slide.
const CLOSE_TRANSITION_TIMEOUT_MS = 450;

export class RightPanelManager {
  /**
   * @param {Object} elements - DOM element references
   * @param {EventBus} eventBus - Event bus for module communication
   */
  constructor(elements, eventBus) {
    this.rightPanel = elements.rightPanel;
    this.rightPanelClose = elements.rightPanelClose;
    this.rightPanelBack = elements.rightPanelBack;
    this.rightPanelTitle = elements.rightPanelTitle;
    this.rightPanelContent = elements.rightPanelContent;
    this.rightPanelStar = elements.rightPanelStar;
    this.rightPanelOptionsMount = elements.rightPanelOptionsMount;
    this.rightPanelMenuMount = elements.rightPanelMenuMount;
    this.rightPanelSecondary = elements.rightPanelSecondary;
    this.rightPanelPrimary = elements.rightPanelPrimary;
    this.resizeHandle = elements.resizeHandle;
    this.mainWrapper = elements.mainWrapper;
    this.chatContainer = elements.chatContainer;
    this.eventBus = eventBus;

    // State
    this._isOpen = false;
    this._currentContentType = null;
    this._currentPayload = null;
    this._drillStack = []; // drill stack (cap 2): [{payload, scrollTop, disposer}]
    this._handoffPending = false;
    this._activeOptionsDropdown = null;
    this._optionsTooltipCleanup = null;
    this._activeHeaderMenu = null;
    this._headerMenuTooltipCleanup = null;
    this._backButtonHandler = null;
    this._secondaryButtonHandler = null;
    this._primaryButtonHandler = null;
    this._starButtonHandler = null;
    this._starButtonState = false;

    // Resize state
    this.isResizing = false;
    this.resizeContainerRect = null;
    this.availableWidth = null;
    this.resizeHandleWidth = 0;
    this.minPanelWidth = 400;
    this.rafId = null;
    this.boundHandleResize = null;
    this.boundStopResize = null;

    // Width state. `_panelWidthPx` is the last width `_writeWidth` actually
    // wrote (the sole writer of `--right-panel-width`); `_userDragged` means the
    // user sized the panel by hand and owns it for the page lifetime;
    // `_nudged` means content asked for a width (`payload.preferredWidth`) and
    // got it, which survives a window resize but not a fresh open of content
    // that expresses no preference.
    this._panelWidthPx = null;
    this._userDragged = false;
    this._nudged = false;

    this.initialize();
  }

  /**
   * Initialize right panel manager
   */
  initialize() {
    // Close button - always closes the panel
    if (this.rightPanelClose) {
      this.rightPanelClose.addEventListener('click', () => this.close());
    }

    // Back button - delegates to per-panel handler
    if (this.rightPanelBack) {
      this.rightPanelBack.addEventListener('click', () => {
        if (this._backButtonHandler) this._backButtonHandler();
      });
    }

    // Secondary button - delegates to per-panel handler
    if (this.rightPanelSecondary) {
      this.rightPanelSecondary.addEventListener('click', () => {
        if (this._secondaryButtonHandler) this._secondaryButtonHandler();
      });
    }

    // Primary button - delegates to per-panel handler
    if (this.rightPanelPrimary) {
      this.rightPanelPrimary.addEventListener('click', () => {
        if (this._primaryButtonHandler) this._primaryButtonHandler();
      });
    }

    // Star toggle - optimistic flip, revert if the handler reports failure
    if (this.rightPanelStar) {
      this.rightPanelStar.addEventListener('click', async () => {
        if (!this._starButtonHandler) return;
        const next = !this._starButtonState;
        this._applyStarButtonState(next);
        const ok = await this._starButtonHandler(next);
        if (!ok) this._applyStarButtonState(!next);
      });
    }

    // Standardized header tooltips (shared Tooltip utility — same styling as
    // the sidebar/toolbar). Close/Back are static; Star reads its label live
    // so the tooltip flips with the starred state on the next hover.
    if (this.rightPanelClose) {
      Tooltip.setup(this.rightPanelClose, { position: 'below' });
    }
    if (this.rightPanelBack) {
      Tooltip.setup(this.rightPanelBack, { position: 'below' });
    }
    if (this.rightPanelStar) {
      Tooltip.setup(this.rightPanelStar, {
        position: 'below',
        getText: () => (this._starButtonState ? 'Unstar' : 'Star')
      });
    }

    // Resize handle drag functionality (desktop only)
    if (this.resizeHandle) {
      this.resizeHandle.addEventListener('mousedown', (e) => {
        if (!this.isMobile() && this._isOpen) {
          this.startResize(e);
        }
      });
    }

    // Handle window resize to detect viewport changes
    window.addEventListener('resize', () => {
      this.handleViewportChange();
    });

    // Listen for right panel events
    if (this.eventBus) {
      this.eventBus.on('right-panel:open', (payload) => {
        this.open(payload);
      });

      this.eventBus.on('right-panel:close', (data) => {
        if (data?.force) {
          this.forceClose({ reason: data.reason || 'navigation' });
        } else {
          this.close();
        }
      });

      this.eventBus.on('right-panel:drill', (payload) => {
        this.drill(payload);
      });

      this.eventBus.on('right-panel:pop', () => {
        this.pop();
      });

      this.eventBus.on('right-panel:update-content', (data) => {
        if (this._isOpen && data.content) {
          this.setContent(data.content);
        }
      });

      this.eventBus.on('right-panel:set-secondary-button', (config) => {
        if (this._isOpen) this.setSecondaryButton(config);
      });

      this.eventBus.on('right-panel:set-options-dropdown', (config) => {
        if (this._isOpen) this.setOptionsDropdown(config);
      });

      this.eventBus.on('right-panel:set-header-menu', (config) => {
        if (this._isOpen) this.setHeaderMenu(config);
      });

      // Cross-surface star sync: a `content:starred` event elsewhere may target
      // the item currently open in the panel. Flip the header star's visible
      // state without touching its persistence handler. No-op when closed or
      // when the current payload has no star button.
      this.eventBus.on('right-panel:set-star', (data) => {
        if (this._isOpen && this.rightPanelStar) {
          this._applyStarButtonState(!!data?.starred);
        }
      });
    }
  }

  /**
   * Build (or tear down) the header disclosure menu — a labelled icon button in
   * the actions row that opens the shared `.options-dropdown` panel.
   *
   * Uses OptionsDropdown's DISCLOSURE shape, not its action-menu shape: the
   * action shape hardcodes an icon-only kebab trigger and gives every row a
   * leading action icon, which fights numbered content. Here the mount div is
   * the positioning wrapper and the button inside it is the adopted trigger.
   *
   * The close-on-activate listener binds to `menuElement`, which persists across
   * opens (`body()` re-renders into it), so it binds once per panel rather than
   * once per open.
   *
   * `showLabel` and `indicator` are read INDIVIDUALLY and both default off, so
   * no existing consumer changes shape. With `showLabel` the trigger renders
   * its label as visible text and becomes an auto-width pill
   * (`.right-panel__menu-btn--labelled` in layout.css); the Tooltip and the
   * now-redundant `aria-label` are skipped, because the button's accessible
   * name is the text it shows. `indicator` adds a small unread dot.
   *
   * @param {Object|null} config - { label, iconId, body, menuClass, showLabel, indicator } or null
   * @private
   */
  _configureHeaderMenu(config) {
    if (this._headerMenuTooltipCleanup) {
      this._headerMenuTooltipCleanup();
      this._headerMenuTooltipCleanup = null;
    }
    if (this._activeHeaderMenu) {
      this._activeHeaderMenu.destroy();
      this._activeHeaderMenu = null;
    }
    if (!this.rightPanelMenuMount) return;
    this.rightPanelMenuMount.innerHTML = '';
    if (!config || typeof config.body !== 'function') {
      this.rightPanelMenuMount.hidden = true;
      return;
    }

    this.rightPanelMenuMount.hidden = false;
    const label = config.label || 'Menu';
    const showLabel = config.showLabel === true;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = showLabel
      ? 'right-panel__menu-btn right-panel__menu-btn--labelled'
      : 'right-panel__menu-btn';
    if (!showLabel) trigger.setAttribute('aria-label', label);
    trigger.innerHTML = `<svg width="20" height="20" aria-hidden="true"><use href="#icon-${config.iconId || 'contents'}"/></svg>`;
    if (showLabel) {
      const labelEl = document.createElement('span');
      labelEl.className = 'right-panel__menu-label';
      labelEl.textContent = label;
      trigger.appendChild(labelEl);
    }
    if (config.indicator) {
      const dot = document.createElement('span');
      dot.className = 'right-panel__menu-dot';
      dot.setAttribute('aria-hidden', 'true');
      trigger.appendChild(dot);
    }
    this.rightPanelMenuMount.appendChild(trigger);

    this._activeHeaderMenu = new OptionsDropdown({
      trigger,
      body: config.body,
      menuClass: config.menuClass || '',
      menuId: 'right-panel-header-menu'
    });

    // Activating a row in a disclosure panel does not close it (this shape binds
    // no item listeners), so close on any button press inside.
    if (this._activeHeaderMenu.menuElement) {
      this._activeHeaderMenu.menuElement.addEventListener('click', (e) => {
        if (e.target.closest('button')) this._activeHeaderMenu?.close();
      });
    }

    // A labelled trigger already says what it is; a tooltip repeating the
    // visible text is noise.
    if (!showLabel) {
      this._headerMenuTooltipCleanup = Tooltip.setup(trigger, {
        position: 'below',
        getText: () => label
      });
    }
  }

  /**
   * Rebuild the header disclosure menu in place without re-opening the panel.
   * Needed by the async guide panels, which emit their payload before the guide
   * has loaded and therefore cannot know their own section list yet.
   * Also the refresh path for `showLabel`/`indicator`: re-emitting the menu
   * config with `indicator` cleared is how the unread dot goes away without
   * reopening the panel.
   * @param {Object|null} config - { label, iconId, body, menuClass, showLabel, indicator } or null
   */
  setHeaderMenu(config) {
    this._configureHeaderMenu(config);
    if (this._currentPayload) {
      this._currentPayload.headerMenu = config || null;
    }
  }

  /**
   * Rebuild the header Options dropdown in place without re-opening the panel
   * (which would re-run the whole content build + re-slide). Used to live-patch
   * one item's label (e.g. Archive ↔ Unarchive) while keeping the star button
   * and body untouched.
   * @param {Object|null} config - { items, onAction } or null to hide
   */
  setOptionsDropdown(config) {
    if (this._activeOptionsDropdown) {
      this._activeOptionsDropdown.destroy();
      this._activeOptionsDropdown = null;
    }
    if (this.rightPanelOptionsMount) {
      this.rightPanelOptionsMount.innerHTML = '';
      if (config && Array.isArray(config.items) && config.items.length) {
        this._activeOptionsDropdown = new OptionsDropdown({
          container: this.rightPanelOptionsMount,
          items: config.items,
          onAction: config.onAction || (() => {})
        });
      }
    }
    if (this._currentPayload) {
      this._currentPayload.optionsDropdown = config || null;
    }
  }

  /**
   * Update the header secondary button without touching content.
   * @param {Object|null} config - { label, onClick } or null to hide
   */
  setSecondaryButton(config) {
    if (!this.rightPanelSecondary) return;
    if (config) {
      this.rightPanelSecondary.textContent = config.label || '';
      this._secondaryButtonHandler = config.onClick || null;
      this.rightPanelSecondary.hidden = false;
    } else {
      this.rightPanelSecondary.textContent = '';
      this._secondaryButtonHandler = null;
      this.rightPanelSecondary.hidden = true;
    }
    if (this._currentPayload) {
      this._currentPayload.secondaryButton = config || null;
    }
  }

  /**
   * Check if viewport is mobile
   * @returns {boolean}
   */
  isMobile() {
    return window.innerWidth < 1020;
  }

  /**
   * Open right panel with the given payload.
   * @param {Object} payload - Panel configuration
   * @param {string} payload.title - Panel title text
   * @param {HTMLElement|null} [payload.titleNode] - Element to mount in the title slot
   *   instead of `title` text (e.g. the questionnaire wizard's question-number pill).
   *   The caller owns the node's content and visibility.
   * @param {'left'|'center'} [payload.titleAlign='left'] - Title alignment on desktop
   * @param {Object|null} [payload.backButton] - Back button config or null to hide
   * @param {Function} payload.backButton.onClick - Click handler for back button
   * @param {Object|null} [payload.starButton] - Star toggle config or null to hide
   * @param {boolean} payload.starButton.starred - Initial pressed/filled state
   * @param {Function} payload.starButton.onClick - `(nextStarred) => boolean|Promise<boolean>`;
   *   persistence lives with the caller, this manager only owns the visual toggle + revert
   * @param {Object|null} [payload.headerMenu] - Header disclosure menu or null to hide.
   *   A labelled icon button in the actions row opening the shared
   *   `.options-dropdown` panel with caller-supplied markup — for read-and-jump
   *   panels (a guide's Contents), not action menus, which use `optionsDropdown`.
   * @param {string} payload.headerMenu.label - Accessible name + tooltip text
   * @param {string} payload.headerMenu.iconId - Sprite symbol id, minus `icon-`
   * @param {Function} payload.headerMenu.body - Returns panel markup on each open
   * @param {string} [payload.headerMenu.menuClass] - Extra class on the panel
   * @param {Object|null} [payload.optionsDropdown] - Options dropdown config or null to hide
   * @param {Array} payload.optionsDropdown.items - Menu items (see OptionsDropdown)
   * @param {Function} payload.optionsDropdown.onAction - Action callback
   * @param {Object|null} [payload.secondaryButton] - Secondary button config or null to hide
   * @param {string} payload.secondaryButton.label - Button label
   * @param {Function} [payload.secondaryButton.onClick] - Click handler
   * @param {Object|null} [payload.primaryButton] - Primary button config or null to hide
   * @param {string} payload.primaryButton.label - Button label
   * @param {Function} payload.primaryButton.onClick - Click handler
   * @param {HTMLElement|string} payload.content - Body content
   * @param {string} [payload.contentType] - Content type tag (e.g. 'session-detail')
   * @param {number} [payload.preferredWidth] - Desktop panel width in px this
   *   content reads best at. On open/swap/drill the panel is set to whichever
   *   is larger of 50/50 or this preferred width (capped at
   *   `available - minPanelWidth`) — the nudge is a floor under the default
   *   split, not a target that can pull the panel narrower than 50/50. Never
   *   shrinks a panel already open at or past the target, and never overrides
   *   a width the user dragged. See `_openWidth`.
   */
  open(payload = {}) {
    // Consumes any armed handoff (see beginHandoff()) regardless of outcome
    // below — this IS the reopen the handoff was waiting for, whether it
    // lands on the already-open cross-fade branch or a fresh slide-in.
    this._handoffPending = false;

    const {
      title = '',
      titleNode = null,
      titleAlign = 'left',
      backButton = null,
      starButton = null,
      headerMenu = null,
      optionsDropdown = null,
      secondaryButton = null,
      primaryButton = null,
      content = null,
      contentType = null,
      onDispose = null
    } = payload;

    // If the panel is already open, cross-fade the content swap rather than
    // replacing instantly. Skip the slide-in animation since the panel is
    // already in place.
    if (this._isOpen) {
      // A normal open replaces the top-level panel context. If the previous
      // view was drilled, discard that stack so the new content can drill.
      this._drainDrillStack();
      this._currentPayload = payload;
      // Ahead of the cross-fade, not inside it: the class is idempotent and
      // visually inert on its own, and applying it here lands the content-type
      // container and the panel's width together rather than 130ms apart.
      this._applyContentTypeClass(contentType);
      this._openWidth(payload, { alreadyOpen: true });
      this._crossFadeSwap(() => {
        this._configureHeader({ title, titleNode, titleAlign, backButton, starButton, headerMenu, optionsDropdown, secondaryButton, primaryButton });
        this._setContent(content, onDispose);
      });

      this._currentContentType = contentType;
      if (this.eventBus) {
        this.eventBus.emit('right-panel:opened', { title, contentType });
      }
      return;
    }

    // Fresh open clears any drill stack from a previous panel context
    this._drillStack = [];

    this._configureHeader({ title, titleNode, titleAlign, backButton, starButton, headerMenu, optionsDropdown, secondaryButton, primaryButton });
    this._setContent(content, onDispose);
    this._applyContentTypeClass(contentType);

    // Desktop vs Mobile behavior
    if (this.isMobile()) {
      this.rightPanel.classList.add('right-panel--open');
    } else {
      this.resizeHandle.classList.add('resize-handle--visible');
      this._openWidth(payload, { alreadyOpen: false });
      this.rightPanel.classList.add('right-panel--open');
    }

    this._isOpen = true;
    this._currentContentType = contentType;
    this._currentPayload = payload;

    if (this.eventBus) {
      this.eventBus.emit('right-panel:opened', { title, contentType });
    }
  }

  /**
   * Cross-fade the panel header + content while the panel stays open.
   * Fades both out, runs `updateFn` to swap state, then fades back in.
   * Uses inline styles to bypass the larger fade-after-slide transition that
   * normally runs on initial open.
   * @private
   */
  _crossFadeSwap(updateFn) {
    const header = this.rightPanel.querySelector('.right-panel__header');
    const content = this.rightPanelContent;
    if (!header || !content) {
      updateFn();
      return;
    }

    const FADE_MS = 120;

    header.style.transition = `opacity ${FADE_MS}ms ease`;
    content.style.transition = `opacity ${FADE_MS}ms ease`;
    header.style.opacity = '0';
    content.style.opacity = '0';

    setTimeout(() => {
      updateFn();

      requestAnimationFrame(() => {
        header.style.opacity = '1';
        content.style.opacity = '1';
      });

      setTimeout(() => {
        header.style.transition = '';
        content.style.transition = '';
        header.style.opacity = '';
        content.style.opacity = '';
      }, FADE_MS + 30);
    }, FADE_MS + 10);
  }

  /**
   * Configure header slots (title, back, star, options, primary).
   * @private
   */
  _configureHeader({ title, titleNode = null, titleAlign, backButton, starButton, headerMenu = null, optionsDropdown, secondaryButton, primaryButton }) {
    // Title — a caller-supplied node replaces the text title entirely.
    // Clearing textContent first also detaches whatever the previous payload mounted.
    if (this.rightPanelTitle) {
      this.rightPanelTitle.textContent = '';
      if (titleNode) {
        this.rightPanelTitle.appendChild(titleNode);
      } else {
        this.rightPanelTitle.textContent = title || '';
      }
      this.rightPanelTitle.classList.toggle('right-panel__title--center', titleAlign === 'center');
    }

    // Back button
    if (this.rightPanelBack) {
      if (backButton) {
        this._backButtonHandler = backButton.onClick || null;
        this.rightPanelBack.hidden = false;
      } else {
        this._backButtonHandler = null;
        this.rightPanelBack.hidden = true;
      }
    }

    // Star toggle
    if (this.rightPanelStar) {
      if (starButton) {
        this._starButtonHandler = starButton.onClick || null;
        this.rightPanelStar.hidden = false;
        this._applyStarButtonState(!!starButton.starred);
      } else {
        this._starButtonHandler = null;
        this.rightPanelStar.hidden = true;
      }
    }

    this._configureHeaderMenu(headerMenu);

    // Options dropdown — destroy existing, create new
    if (this._optionsTooltipCleanup) {
      this._optionsTooltipCleanup();
      this._optionsTooltipCleanup = null;
    }
    if (this._activeOptionsDropdown) {
      this._activeOptionsDropdown.destroy();
      this._activeOptionsDropdown = null;
    }
    if (this.rightPanelOptionsMount) {
      this.rightPanelOptionsMount.innerHTML = '';
      if (optionsDropdown && Array.isArray(optionsDropdown.items) && optionsDropdown.items.length) {
        this._activeOptionsDropdown = new OptionsDropdown({
          container: this.rightPanelOptionsMount,
          items: optionsDropdown.items,
          onAction: optionsDropdown.onAction || (() => {})
        });
        // Tooltip for the icon-only kebab, matching the panel's other header
        // buttons (Back/Star/Close). Wired here (not in the shared
        // OptionsDropdown component) so it stays scoped to the right panel;
        // torn down above when the dropdown is rebuilt on the next open.
        if (this._activeOptionsDropdown.buttonElement) {
          this._optionsTooltipCleanup = Tooltip.setup(
            this._activeOptionsDropdown.buttonElement,
            { position: 'below', getText: () => 'Options' }
          );
        }
      }
    }

    // Secondary button
    if (this.rightPanelSecondary) {
      if (secondaryButton) {
        this.rightPanelSecondary.textContent = secondaryButton.label || '';
        this._secondaryButtonHandler = secondaryButton.onClick || null;
        this.rightPanelSecondary.hidden = false;
      } else {
        this.rightPanelSecondary.textContent = '';
        this._secondaryButtonHandler = null;
        this.rightPanelSecondary.hidden = true;
      }
    }

    // Primary button
    if (this.rightPanelPrimary) {
      if (primaryButton) {
        this.rightPanelPrimary.textContent = primaryButton.label || '';
        this._primaryButtonHandler = primaryButton.onClick || null;
        this.rightPanelPrimary.hidden = false;
      } else {
        this.rightPanelPrimary.textContent = '';
        this._primaryButtonHandler = null;
        this.rightPanelPrimary.hidden = true;
      }
    }
  }

  /**
   * Apply the star toggle's visual (pressed/filled) state without touching
   * the persistence handler. Used both for initial render and for the
   * optimistic-toggle + revert-on-failure flow.
   * @private
   * @param {boolean} starred
   */
  _applyStarButtonState(starred) {
    if (!this.rightPanelStar) return;
    this._starButtonState = starred;
    this.rightPanelStar.classList.toggle('right-panel__star--active', starred);
    this.rightPanelStar.setAttribute('aria-pressed', starred ? 'true' : 'false');
    const label = starred ? 'Unstar this conversation' : 'Star this conversation';
    this.rightPanelStar.setAttribute('aria-label', label);
  }

  /**
   * Run and clear the current content disposer, if any. Invoked before content
   * is replaced (open/cross-fade/drill) and on close, so mounted widgets (e.g.
   * a BrainViz) get torn down rather than leaked when innerHTML is wiped.
   * @private
   */
  _disposeContent() {
    if (this._contentDisposer) {
      try { this._contentDisposer(); } catch (e) { /* disposer must not block panel */ }
      this._contentDisposer = null;
    }
  }

  /**
   * Fire every disposer `drill()` suspended, then clear the stack. Called from
   * the three paths that DISCARD the drill stack rather than popping it —
   * `close()`, `forceClose()`, and a top-level `open()` over a drilled view.
   * Without this the suspended parents would never be torn down at all.
   *
   * A throwing disposer must not strand the ones behind it, so each is
   * isolated the same way `_disposeContent` isolates the current one.
   * @private
   */
  _drainDrillStack() {
    for (const frame of this._drillStack) {
      if (typeof frame?.disposer === 'function') {
        try { frame.disposer(); } catch (e) { /* disposer must not block panel */ }
      }
    }
    this._drillStack = [];
  }

  /**
   * Replace the panel body content.
   * @param {HTMLElement|string} content
   * @param {Function} [onDispose] - cleanup invoked before the next swap / on close
   * @private
   */
  _setContent(content, onDispose = null) {
    const contentInner = this.rightPanelContent?.querySelector('.right-panel__content-inner');
    if (!contentInner) return;
    this._disposeContent();
    contentInner.innerHTML = '';
    if (typeof content === 'string') {
      contentInner.innerHTML = content;
    } else if (content instanceof HTMLElement) {
      contentInner.appendChild(content);
    }
    this._contentDisposer = typeof onDispose === 'function' ? onDispose : null;
    // Every swap starts its page at the top. The scroller is never unmounted —
    // a closed panel is only `visibility: hidden` — so its offset survives a
    // close, and a same-task empty-then-refill never gives layout a chance to
    // clamp it. Without this, a guide opened fresh after a scrolled session
    // was closed lands partway down. `pop()` restores the parent's snapshotted
    // offset in a later frame, so this reset is harmless there.
    if (this.rightPanelContent) this.rightPanelContent.scrollTop = 0;
  }

  /**
   * Swap the content-type class on the panel root.
   * @private
   */
  _applyContentTypeClass(contentType) {
    this.rightPanel.classList.forEach((className) => {
      if (className.startsWith('right-panel--content-')) {
        this.rightPanel.classList.remove(className);
      }
    });
    if (contentType) {
      this.rightPanel.classList.add(`right-panel--content-${contentType}`);
    }
  }

  /**
   * Whether a drill is currently allowed (panel is open and below the depth cap).
   * Caller-side check used to fall back to a full navigate when at depth cap (2).
   * Depth 2 supports chains like session notes → transcript.
   * @returns {boolean}
   */
  canDrill() {
    return this._isOpen && this._drillStack.length < 2;
  }

  /**
   * Drill into a sub-detail. Snapshots current state, fades content, applies new payload,
   * auto-wires the back button to pop().
   *
   * A drill SUSPENDS the parent rather than replacing it: its content node and
   * its `onDispose` both survive on the stack, so the view the user comes back
   * to is the one they left, still live. The parent is detached from the
   * document while drilled, which means any work it has in flight lands on a
   * detached tree — fine for DOM writes, but a view that MEASURES layout at
   * mount would read zeros, so build first and measure on restore.
   *
   * @param {Object} payload - Same shape as `right-panel:open` payload
   */
  drill(payload) {
    if (!this._isOpen) {
      console.warn('[RightPanelManager] drill() called while panel closed — ignoring');
      return;
    }
    if (this._drillStack.length >= 2) {
      console.warn('[RightPanelManager] drill() called at depth cap — ignoring');
      return;
    }

    const scrollTop = this.rightPanelContent?.scrollTop || 0;
    // SUSPEND the parent's disposer rather than firing it. A drill keeps the
    // parent's content NODE on the stack for `pop()` to re-append, so disposing
    // it here would tear down the live components inside a node we are about to
    // restore — the user comes back to a hollow shell (the understanding map's
    // polar chart empties its own mount, so back returned rows with no chart).
    // Detaching it first makes the `_setContent()` below a no-op for it. The
    // frame owns it from here: `pop()` re-registers it, and every path that
    // DISCARDS the stack instead drains it (`_drainDrillStack`).
    const suspended = this._contentDisposer;
    this._contentDisposer = null;
    this._drillStack.push({ payload: this._currentPayload, scrollTop, disposer: suspended });

    const drilledPayload = {
      ...payload,
      backButton: { onClick: () => this.pop() }
    };

    // Ahead of the fade, for the same reason as the already-open branch of
    // open(): container and width land together, not 150ms apart.
    this._applyContentTypeClass(drilledPayload.contentType);
    this._openWidth(drilledPayload, { alreadyOpen: true });

    this._fadeContentSwap(() => {
      this._configureHeader({
        title: drilledPayload.title || '',
        titleNode: drilledPayload.titleNode || null,
        titleAlign: drilledPayload.titleAlign || 'left',
        backButton: drilledPayload.backButton,
        starButton: drilledPayload.starButton || null,
        headerMenu: drilledPayload.headerMenu || null,
        optionsDropdown: drilledPayload.optionsDropdown || null,
        secondaryButton: drilledPayload.secondaryButton || null,
        primaryButton: drilledPayload.primaryButton || null
      });
      this._setContent(drilledPayload.content, drilledPayload.onDispose);
      this._currentPayload = drilledPayload;
      this._currentContentType = drilledPayload.contentType || null;
    });
  }

  /**
   * Pop the drill stack: restore the previous panel state.
   */
  pop() {
    if (this._drillStack.length === 0) return;

    const snapshot = this._drillStack.pop();
    const payload = snapshot.payload || {};

    this._fadeContentSwap(() => {
      this._configureHeader({
        title: payload.title || '',
        titleNode: payload.titleNode || null,
        titleAlign: payload.titleAlign || 'left',
        backButton: payload.backButton || null,
        starButton: payload.starButton || null,
        headerMenu: payload.headerMenu || null,
        optionsDropdown: payload.optionsDropdown || null,
        secondaryButton: payload.secondaryButton || null,
        primaryButton: payload.primaryButton || null
      });
      // Re-register what `drill()` suspended, so a later close still tears the
      // restored view down — exactly once, and never while it is on screen.
      this._setContent(payload.content, snapshot.disposer || payload.onDispose || null);
      this._applyContentTypeClass(payload.contentType);
      this._currentPayload = payload;
      this._currentContentType = payload.contentType || null;

      // Restore scroll position after the content is re-attached
      requestAnimationFrame(() => {
        if (this.rightPanelContent) {
          this.rightPanelContent.scrollTop = snapshot.scrollTop || 0;
        }
      });
    });
  }

  /**
   * Fade out the content area, run swapFn, fade back in.
   * Uses inline styles to override the panel-open transition timing.
   * @private
   */
  _fadeContentSwap(swapFn) {
    if (!this.rightPanelContent) {
      swapFn();
      return;
    }
    const el = this.rightPanelContent;
    el.style.transition = 'opacity 150ms ease';
    el.style.transitionDelay = '0ms';
    el.style.opacity = '0';
    setTimeout(() => {
      swapFn();
      el.style.opacity = '1';
      setTimeout(() => {
        el.style.transition = '';
        el.style.transitionDelay = '';
        el.style.opacity = '';
      }, 150);
    }, 150);
  }

  /**
   * Tear down per-panel header state on close.
   * @private
   */
  _teardownHeaderState() {
    if (this._activeOptionsDropdown) {
      this._activeOptionsDropdown.destroy();
      this._activeOptionsDropdown = null;
    }
    this._backButtonHandler = null;
    this._secondaryButtonHandler = null;
    this._primaryButtonHandler = null;
    this._starButtonHandler = null;
    this._starButtonState = false;
    if (this.rightPanelBack) this.rightPanelBack.hidden = true;
    if (this.rightPanelStar) {
      this.rightPanelStar.hidden = true;
      this.rightPanelStar.classList.remove('right-panel__star--active');
      this.rightPanelStar.setAttribute('aria-pressed', 'false');
    }
    if (this.rightPanelSecondary) {
      this.rightPanelSecondary.hidden = true;
      this.rightPanelSecondary.textContent = '';
    }
    if (this.rightPanelPrimary) {
      this.rightPanelPrimary.hidden = true;
      this.rightPanelPrimary.textContent = '';
    }
    if (this.rightPanelOptionsMount) {
      this.rightPanelOptionsMount.innerHTML = '';
    }
    this._configureHeaderMenu(null);
    if (this.rightPanelTitle) {
      // Also detaches any caller-supplied titleNode (see _configureHeader)
      this.rightPanelTitle.textContent = '';
      this.rightPanelTitle.classList.remove('right-panel__title--center');
    }
  }

  /**
   * Arm a pending cross-page handoff. Call this instead of close() when a
   * navigation is expected to immediately reopen an equivalent detail on the
   * destination page (e.g. a starred-row click that crosses from /search to
   * /notes) — the panel stays visually open and state-intact through the
   * page transition, so the destination's own open() call lands on the
   * existing already-open cross-fade branch above instead of a fresh
   * slide-in. Suppresses the next close() (any redundant close from the
   * outgoing section's own deactivate() is a harmless no-op, same as
   * today); consumed by the next open() or closeStaleHandoff().
   */
  beginHandoff() {
    this._handoffPending = true;
  }

  /**
   * @returns {boolean} Whether a handoff armed by beginHandoff() is still
   * waiting on an open() or closeStaleHandoff() to consume it.
   */
  hasPendingHandoff() {
    return this._handoffPending;
  }

  /**
   * Safety net for a destination page's "item not found" fallback (stale or
   * deleted id): if a handoff armed by beginHandoff() never resolves into an
   * open() call, this force-closes the stale panel left open from the
   * previous page. No-op when no handoff is pending, so it never touches an
   * already-valid open panel (e.g. a same-page invalid-param edit).
   */
  closeStaleHandoff() {
    if (!this._handoffPending) return;
    this.forceClose({ reason: 'stale-handoff' });
  }

  /**
   * Close right panel with animation
   */
  close() {
    if (this._handoffPending) return;
    if (!this._isOpen) return;

    const closedContentType = this._currentContentType;

    if (this.resizeHandle) {
      this.resizeHandle.classList.add('resize-handle--hiding');
      this.resizeHandle.classList.remove('resize-handle--visible');
      setTimeout(() => {
        this.resizeHandle.classList.remove('resize-handle--hiding');
      }, 150);
    }

    this.rightPanel.classList.remove('right-panel--open');
    this._isOpen = false;
    this._currentContentType = null;
    this._currentPayload = null;
    this._drainDrillStack();

    this.rightPanel.classList.forEach((className) => {
      if (className.startsWith('right-panel--content-')) {
        this.rightPanel.classList.remove(className);
      }
    });

    this._disposeContent();
    this._teardownHeaderState();

    this._emitClosedWhenSlideEnds(closedContentType);
  }

  /**
   * Emit `right-panel:closed` when the slide-out actually finishes.
   *
   * Consumers act on a settled layout — the chat window finishes re-widening
   * only as the panel's width transition ends, and the questionnaire settle
   * choreography measures against it. A fixed timer matched to the CSS duration
   * lands a frame or two either side of the real end; transitionend does not.
   *
   * @param {string|null} contentType - contentType of the panel being closed
   * @private
   */
  _emitClosedWhenSlideEnds(contentType) {
    const panel = this.rightPanel;
    let emitted = false;

    const finish = () => {
      if (emitted) return;
      emitted = true;
      clearTimeout(timeout);
      panel?.removeEventListener('transitionend', onTransitionEnd);
      this.eventBus?.emit('right-panel:closed', { contentType });
    };

    const onTransitionEnd = (event) => {
      if (event.target !== panel) return; // ignore bubbling child transitions
      if (!CLOSE_TRANSITION_PROPERTIES.includes(event.propertyName)) return;
      finish();
    };

    panel?.addEventListener('transitionend', onTransitionEnd);
    const timeout = setTimeout(finish, CLOSE_TRANSITION_TIMEOUT_MS);
  }

  /**
   * Force close right panel immediately without animation
   * Used when navigating away from chat to other pages
   * @param {Object} options - Close options
   * @param {string} options.reason - Reason for closing ('navigation' or 'user_action')
   */
  forceClose(options = {}) {
    this._handoffPending = false;
    if (!this._isOpen) return;

    const { reason = 'user_action' } = options;
    const closedContentType = this._currentContentType;

    this.rightPanel.classList.add('right-panel--no-transition');
    void this.rightPanel.offsetHeight;

    if (this.resizeHandle) {
      this.resizeHandle.classList.remove('resize-handle--visible', 'resize-handle--hiding');
    }

    this.rightPanel.classList.remove('right-panel--open');
    this._isOpen = false;
    this._currentContentType = null;
    this._currentPayload = null;
    this._drainDrillStack();

    this.rightPanel.classList.forEach((className) => {
      if (className.startsWith('right-panel--content-')) {
        this.rightPanel.classList.remove(className);
      }
    });

    this._disposeContent();
    this._teardownHeaderState();

    requestAnimationFrame(() => {
      this.rightPanel.classList.remove('right-panel--no-transition');
    });

    if (this.eventBus) {
      this.eventBus.emit('right-panel:closed', { reason, contentType: closedContentType });
    }
  }

  /**
   * Set/update panel content while open
   * @param {HTMLElement|string} content - New content
   */
  setContent(content) {
    this._setContent(content);
  }

  /**
   * Check if panel is currently open
   * @returns {boolean}
   */
  isOpen() {
    return this._isOpen;
  }

  // ===== RESIZE FUNCTIONALITY =====

  /**
   * Start resize drag operation
   * @param {MouseEvent} e - Mouse event
   */
  startResize(e) {
    e.preventDefault();
    this.isResizing = true;

    this.resizeContainerRect = this.rightPanel.parentElement.getBoundingClientRect();
    this.resizeHandleWidth = this.resizeHandle.getBoundingClientRect().width;
    this.availableWidth = this.resizeContainerRect.width - this.resizeHandleWidth;

    this.mainWrapper.classList.add('main-wrapper--resizing');
    this.resizeHandle.classList.add('resize-handle--dragging');
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    this.boundHandleResize = (e) => this.handleResize(e);
    this.boundStopResize = () => this.stopResize();

    document.addEventListener('mousemove', this.boundHandleResize);
    document.addEventListener('mouseup', this.boundStopResize);
  }

  /**
   * Handle resize drag
   * @param {MouseEvent} e - Mouse event
   */
  handleResize(e) {
    if (!this.isResizing) return;

    // Set synchronously, before the rAF is scheduled: stopResize() cancels a
    // pending rAF, so a flag raised inside the callback (or in stopResize)
    // would miss a drag that ends within the same frame. One mousemove during
    // a drag means the width is the user's from here on.
    this._userDragged = true;

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      const mousePosition = e.clientX - this.resizeContainerRect.left;
      const mainContentWidth = mousePosition;
      const rightPanelWidth = this.availableWidth - mainContentWidth;

      if (mainContentWidth >= this.minPanelWidth && rightPanelWidth >= this.minPanelWidth) {
        this._writeWidth(rightPanelWidth);
      }
    });
  }

  /**
   * Stop resize drag operation
   */
  stopResize() {
    this.isResizing = false;

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.mainWrapper.classList.remove('main-wrapper--resizing');
    this.resizeHandle.classList.remove('resize-handle--dragging');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    document.removeEventListener('mousemove', this.boundHandleResize);
    document.removeEventListener('mouseup', this.boundStopResize);
  }

  /**
   * Width available to the split view: the panel's container minus the drag handle.
   * @returns {number} Available width in px
   * @private
   */
  _available() {
    const containerWidth = this.rightPanel?.parentElement?.getBoundingClientRect().width || 0;
    const handleWidth = this.resizeHandle?.getBoundingClientRect().width || 0;
    return containerWidth - handleWidth;
  }

  /**
   * The SOLE writer of `--right-panel-width`. Clamps to the CSS floor
   * (`--min-panel-width: 350px`) so `_panelWidthPx` never records a width the
   * panel does not actually have, then records what was written.
   * @param {number} px - Requested panel width in px
   * @private
   */
  _writeWidth(px) {
    const width = Math.max(px, 350);
    document.documentElement.style.setProperty('--right-panel-width', `${width}px`);
    this._panelWidthPx = width;
  }

  /**
   * Clamp a candidate width into the legal band for the space available right
   * now. A non-finite candidate falls back to the 50/50 split: writing `NaNpx`
   * would make the custom property invalid, `var()` would fall through to
   * `width: auto`, and the panel would shrink-wrap to its CSS floor.
   * @param {number|null} px - Candidate width in px
   * @returns {number} A finite width in px
   * @private
   */
  _clampWidthPx(px) {
    const available = this._available();
    const candidate = Number.isFinite(px) ? px : available / 2;
    const max = Math.max(this.minPanelWidth, available - this.minPanelWidth);
    return Math.min(Math.max(candidate, this.minPanelWidth), max);
  }

  /**
   * Opening width policy: clamp, then honour the content's `preferredWidth`
   * (the "nudge") when the user has not sized the panel by hand. The nudge is
   * a floor under the 50/50 split, not a set-point — the auto-set width is
   * `max(50%, min(preferredWidth, available - minPanelWidth))`, so a wide
   * viewport where half already exceeds the preferred width stays at 50/50.
   * Desktop only — mobile is a full-width overlay with no width state at all.
   * @param {Object} [payload] - The payload being opened, swapped or drilled in
   * @param {number} [payload.preferredWidth] - Width in px this content reads best at
   * @param {Object} options
   * @param {boolean} options.alreadyOpen - True for a swap/drill into an open panel
   * @private
   */
  _openWidth(payload = {}, { alreadyOpen = false } = {}) {
    if (this.isMobile()) return;

    // 1. Clamp unconditionally: handleViewportChange only runs while the panel
    //    is open, so the viewport may have narrowed underneath a closed panel.
    this._writeWidth(this._clampWidthPx(this._panelWidthPx));

    // 2. A width the user dragged is theirs for the page lifetime.
    if (this._userDragged) return;

    const preferred = Number(payload?.preferredWidth);
    if (Number.isFinite(preferred) && preferred > 0) {
      // 3. The nudge is a FLOOR under the 50/50 split, not a set-point: the
      //    divider starts at 50/50, and only moves left to give the content
      //    its preferred width when 50% would be less than that (capped at
      //    `available - minPanelWidth`). The auto-set width never comes out
      //    below 50%.
      const target = Math.max(
        this._available() / 2,
        Math.min(preferred, this._available() - this.minPanelWidth)
      );
      // Never shrink a panel the reader is already inside.
      if (alreadyOpen && target <= this._panelWidthPx) return;
      this._writeWidth(target);
      this._nudged = true;
      return;
    }

    // 4. No preference. A FRESH open returns to 50/50 — the nudge is scoped to
    //    the content that asked for it and must not follow the reader into a
    //    session or insight panel. A swap inside an open panel keeps the width
    //    the reader is currently reading at (never snap back mid-reading).
    if (!alreadyOpen) {
      this.resetWidths();
      this._nudged = false;
    }
  }

  /**
   * Re-clamp the current width after a viewport change. A panel that was
   * dragged or nudged keeps its pixel width (clamped into the new space); one
   * that is still at the default returns to 50/50. Mobile writes nothing.
   * @private
   */
  _clampWidth() {
    if (this.isMobile()) return;
    if (this._userDragged || this._nudged) {
      this._writeWidth(this._clampWidthPx(this._panelWidthPx));
      return;
    }
    this.resetWidths();
  }

  /**
   * Reset panel widths to 50/50 split
   */
  resetWidths() {
    this._writeWidth(this._available() / 2);
  }

  /**
   * Handle viewport size changes
   */
  handleViewportChange() {
    if (this.isMobile() && this._isOpen) {
      this.resizeHandle.classList.remove('resize-handle--visible');
      this._clampWidth();
    } else if (!this.isMobile() && this._isOpen) {
      this.resizeHandle.classList.add('resize-handle--visible');
      this._clampWidth();
    }
  }
}
